import { useEffect, useMemo, useState, useRef } from "react";
import api, { formatApiError } from "../lib/api";
import { canonComune } from "../lib/comuni";
import { addressCase } from "../lib/addressCase";
import { fixTitoli } from "../lib/titoli";
import { formatDate, formatDateTime, formatDateTimeSec } from "../lib/format";
import RichTextEditor, { invalidatePrintMarginsCache, editorOverflowsA4, OVERFLOW_MSG } from "./RichTextEditor";
import TemplateAIButton from "./TemplateAIButton";
import SendLetterEmailDialog from "./SendLetterEmailDialog";
import { FirmaRemotaDialog } from "./FirmaRemotaDialog";
import { PenLine } from "lucide-react";
import PdfPreviewModal from "./PdfPreviewModal";
import VarsCatalogPanel from "./VarsCatalogPanel";
import { FirmaPicker } from "./FirmaPicker";
import { AiLetterButton } from "./AiLetterDialog";
import ResizableVarsPanel from "./ResizableVarsPanel";
import { sediList, downloadPdf, getCachedLetterPdfB64, hashLetterKey } from "../lib/letterPdf";
import { LetterA4Preview } from "./LetterA4Preview";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import FolderColorPicker from "./FolderColorPicker";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import {
  FileText, Folder, FolderOpen, Pencil, ChevronRight, ChevronDown, FileType2,
  Trash2, Mail, Printer, Download, Save, ArrowLeft, Loader2, Copy, History, RotateCcw, Plus, FolderInput, Eye, Check, Filter, CheckCircle2, Star,
} from "lucide-react";
import { toast } from "sonner";
import { fetchTipologie, asPairs, DEFAULT_TIPOLOGIE_SINISTRO } from "../lib/tipologieSinistro";

function TipiPraticaSelect({ value, onChange, tipi }) {
  const [open, setOpen] = useState(false);
  const list = value || [];
  const toggle = (code) => {
    const set = new Set(list);
    if (set.has(code)) set.delete(code); else set.add(code);
    onChange(Array.from(set));
  };
  return (
    <div>
      <Label className="text-slate-300 text-xs">Tipo pratica</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" data-testid="template-tipi" className="mt-1 h-9 border-slate-700 text-slate-200 hover:bg-slate-800 justify-between min-w-[180px]">
            <span className="truncate">{list.length ? `${list.length} tipo/i selezionati` : "Tutti i tipi"}</span>
            <ChevronDown className="h-4 w-4 ml-1 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 max-h-72 overflow-y-auto bg-[#121824] border-slate-700 text-slate-100 p-2">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono font-bold mb-2 px-1">Tipi pratica del template</div>
          {(tipi || []).map(([code, label]) => {
            const checked = list.includes(code);
            return (
              <button key={code} type="button" onClick={() => toggle(code)} data-testid={`template-tipo-${code}`}
                className="w-full flex items-center gap-2 px-1.5 py-1.5 rounded hover:bg-slate-800 text-left">
                <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${checked ? "bg-amber-500 border-amber-500" : "border-slate-500"}`}>
                  {checked && <Check className="h-3 w-3 text-slate-900" />}
                </span>
                <span className="text-xs text-slate-200"><span className="font-mono text-amber-400">{code}</span> · {label}</span>
              </button>
            );
          })}
          {list.length > 0 && <button type="button" onClick={() => onChange([])} data-testid="template-tipi-clear" className="mt-1 w-full text-[11px] text-slate-400 hover:text-amber-400 py-1">Rimuovi tutti</button>}
        </PopoverContent>
      </Popover>
    </div>
  );
}

const TABS = [  { k: "stampe", l: "STAMPE" },
  { k: "impostazioni", l: "IMPOSTAZIONI DI STAMPE" },
  { k: "mail", l: "CONFIGURA MAIL GENERALE" },
  { k: "firmati", l: "MODELLI FIRMATI" },
];

function TabPanel({ title, children }) {
  return (
    <div className="pgp-card p-8 text-center" data-testid="tab-placeholder">
      <h3 className="font-head font-semibold text-slate-100">{title}</h3>
      <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">{children}</p>
    </div>
  );
}

// Normalizza le categorie variabili: espande le eventuali sottocategorie (subcats)
// in categorie flat con vars sempre come array, evitando crash quando manca "vars".
function normalizeCats(cats) {
  if (!Array.isArray(cats)) return [];
  return cats.flatMap((c) => {
    if (c && Array.isArray(c.subcats)) {
      return c.subcats.map((sc) => ({ title: `${c.title} · ${sc.title}`, vars: Array.isArray(sc.vars) ? sc.vars : [] }));
    }
    return [{ ...c, vars: Array.isArray(c?.vars) ? c.vars : [] }];
  });
}

export default function GestioneLettereDialog({ open, onOpenChange, pratica, assistitoKey = "principale", assistitoName }) {
  const isOwner = false; // gestione template/cartelle avviene nella pagina "Gestione Lettere"; qui rinomina è consentita al titolare via ruolo lato server
  const [tab, setTab] = useState("stampe");
  const [tipiPratica, setTipiPratica] = useState(asPairs(DEFAULT_TIPOLOGIE_SINISTRO));
  useEffect(() => { fetchTipologie().then((l) => setTipiPratica(asPairs(l))); }, []);
  const [folders, setFolders] = useState([]);
  const [leftWidth, setLeftWidth] = useState(() => {
    const s = typeof window !== "undefined" ? window.localStorage.getItem("gl_folders_width") : null;
    const n = s ? parseInt(s, 10) : NaN;
    return Number.isFinite(n) ? n : 300;
  });
  useEffect(() => { try { window.localStorage.setItem("gl_folders_width", String(leftWidth)); } catch { /* */ } }, [leftWidth]);
  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = leftWidth;
    const onMove = (ev) => setLeftWidth(Math.max(240, Math.min(900, startW + (ev.clientX - startX))));
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); document.body.style.userSelect = ""; };
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };
  const [templates, setTemplates] = useState([]);
  const [cestinoTpls, setCestinoTpls] = useState([]);
  const [showCestino, setShowCestino] = useState(false);
  const [letters, setLetters] = useState([]);
  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [varCategories, setVarCategories] = useState([]);
  const [varOpen, setVarOpen] = useState({});
  const toggleCat = (t) => setVarOpen((s) => ({ ...s, [t]: !s[t] }));
  const [catalogOpen, setCatalogOpen] = useState(() => localStorage.getItem("pgp_gl_vars_catalog") !== "0");
  const toggleCatalog = () => setCatalogOpen((o) => { localStorage.setItem("pgp_gl_vars_catalog", o ? "0" : "1"); return !o; });

  // editor state
  const [editing, setEditing] = useState(null); // {nome, content}
  const [saving, setSaving] = useState(false);
  const [saveChoice, setSaveChoice] = useState(null); // { renaming: bool, newName: string } quando si salva una lettera già esistente
  const [emailOpen, setEmailOpen] = useState(false);
  const [firmaCtx, setFirmaCtx] = useState(null);
  const [emailCtx, setEmailCtx] = useState(null); // { nome, content, margins, withHead }
  const [letterPdfDoc, setLetterPdfDoc] = useState(null);
  const [sedeKey, setSedeKey] = useState("principal");
  const DEF_MARGINS = { top: 20, right: 15, bottom: 15, left: 20 };
  const [editorMargins, setEditorMargins] = useState(DEF_MARGINS);
  const globalMargins = useMemo(() => {
    const pm = studio?.settings?.print_margins;
    return pm && typeof pm.top === "number" ? { top: pm.top, right: pm.right, bottom: pm.bottom, left: pm.left } : DEF_MARGINS;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studio]);

  // print margins settings (tab "IMPOSTAZIONI DI STAMPE")
  const PM_DEF = { top: 20, bottom: 15, left: 20, right: 15 };
  const [pm, setPmState] = useState(PM_DEF);
  const [savingPm, setSavingPm] = useState(false);
  useEffect(() => {
    const g = studio?.settings?.print_margins;
    if (g && typeof g.top === "number") setPmState({ top: g.top, bottom: g.bottom, left: g.left, right: g.right });
  }, [studio]);
  const setPm = (k, v) => setPmState((p) => ({ ...p, [k]: v }));
  const savePrintMargins = async () => {
    setSavingPm(true);
    try {
      await api.patch("/studio/settings", { print_margins: pm });
      invalidatePrintMarginsCache();
      const s = await api.get("/studio/me");
      setStudio(s.data);
      toast.success("Margini di stampa salvati");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSavingPm(false); }
  };

  // preview state
  const [preview, setPreview] = useState(null); // saved letter
  const [previewHead, setPreviewHead] = useState("con");
  const [previewRendered, setPreviewRendered] = useState(null);
  const [previewRendering, setPreviewRendering] = useState(false);
  const [versionsLetter, setVersionsLetter] = useState(null);

  // template editor state
  const [templateEditing, setTemplateEditing] = useState(null); // { id, title, content, folder_id }
  const [filterTipo, setFilterTipo] = useState("__all__");
  const [folderPicker, setFolderPicker] = useState(null); // { t, mode: 'move' | 'copy' }
  const [savingTpl, setSavingTpl] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const tplEditorRef = useRef(null);
  const letEditorRef = useRef(null);
  // Mappa chiave -> valore reale risolto dal pannello variabili (usata per l'editor lettera).
  const varValueMap = useMemo(() => {
    const m = {};
    (varCategories || []).forEach((cat) => (cat.vars || []).forEach((v) => { m[v.key] = v.value || ""; }));
    return m;
  }, [varCategories]);
  // Compagnie presenti nella pratica, per il pulsante "Inserisci destinatario pratica" dell'editor lettera.
  const destinatariCompagnie = useMemo(() => {
    const out = [];
    (varCategories || []).forEach((cat) => {
      if (!cat?.title || !cat.title.startsWith("Compagnia")) return;
      const get = (suffix) => {
        const v = (cat.vars || []).find((x) => x.key && x.key.endsWith("." + suffix));
        return v ? (v.value || "") : "";
      };
      const nameFromTitle = cat.title.includes(" - ") ? cat.title.split(" - ").slice(1).join(" - ") : "";
      const rs = get("ragione_sociale") || nameFromTitle;
      if (!rs) return;
      out.push({
        label: cat.title,
        ragione_sociale: rs,
        indirizzo: get("indirizzo"),
        numero_civico: get("numero_civico"),
        cap: get("cap"),
        comune: get("comune"),
        provincia: get("provincia"),
        pec: get("pec"),
      });
    });
    return out;
  }, [varCategories]);
  const fmtVar = (val, fmt) => {
    const s = val == null ? "" : String(val);
    if (fmt === "upper") return s.toUpperCase();
    if (fmt === "lower") return s.toLowerCase();
    if (fmt === "title") {
      const place = canonComune(s) || addressCase(s);
      if (place) return place;
      // Sentence case: minuscolo, poi maiuscola a inizio testo e dopo ogni punto (. ! ?).
      return fixTitoli(s.toLowerCase().replace(/(^\s*|[.!?]+\s*)(\p{Ll})/gu, (m, a, b) => a + b.toUpperCase()));
    }
    if (fmt === "titlecase") {
      const place = canonComune(s) || addressCase(s);
      if (place) return place;
      // Title case (Nome e Cognome): prima lettera di ogni parola maiuscola, resto minuscolo.
      return fixTitoli(s.toLowerCase().replace(/(?<!\p{L})(\p{L})/gu, (c) => c.toUpperCase()));
    }
    if (s && s !== s.toUpperCase()) return canonComune(s) || addressCase(s) || s;
    return s;
  };
  // Testimoni multipli: indici (1..N) presenti tra le variabili della pratica.
  const testiIdx = useMemo(() => {
    const s = new Set();
    Object.keys(varValueMap).forEach((k) => { const m = k.match(/^testimone_(\d+)\./); if (m) s.add(Number(m[1])); });
    return [...s].sort((a, b) => a - b);
  }, [varValueMap]);
  const [testiMode, setTestiMode] = useState(null); // null | "each" | "all"
  const [testiAsk, setTestiAsk] = useState(null); // { field, fmt }
  useEffect(() => { if (!editing) { setTestiMode(null); setTestiAsk(null); } }, [editing]);
  const escHtml = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const TESTI_TOKEN = /\{\{testimone\.([\w.]+?)(?::(\w+))?\}\}/g;
  const resolveTesti = (content, i) => String(content || "").replace(TESTI_TOKEN, (m, f, fmt) => escHtml(fmtVar(varValueMap[`testimone_${i}.${f}`], fmt)));
  const outContent = (c) => (testiMode === "each" && testiIdx.length ? resolveTesti(c, testiIdx[0]) : c);
  const insertTesti = (mode, field, fmt) => {
    const ed = letEditorRef.current;
    if (!ed) return;
    if (mode === "each") {
      ed.chain().focus().insertContent("{{testimone." + field + (fmt ? ":" + fmt : "") + "}}").run();
      return;
    }
    const vals = testiIdx.map((i) => fmtVar(varValueMap[`testimone_${i}.${field}`], fmt)).filter((v) => v);
    ed.chain().focus().insertContent(vals.map(escHtml).join("<br>")).run();
  };
  const chooseTestiMode = (mode) => {
    const ask = testiAsk;
    setTestiMode(mode); setTestiAsk(null);
    if (ask) setTimeout(() => insertTesti(mode, ask.field, ask.fmt), 0);
  };
  const insertVar = (key, fmt) => {
    // Editor TEMPLATE: inserisce il token riutilizzabile {{...}}.
    // Editor LETTERA (dentro pratica): inserisce direttamente il dato reale già formattato.
    if (templateEditing) {
      const token = "{{" + key + (fmt ? ":" + fmt : "") + "}}";
      tplEditorRef.current?.chain().focus().insertContent(token).run();
      return;
    }
    const tm = key.match(/^testimone_\d+\.(.+)$/);
    if (tm && testiIdx.length > 1) {
      if (!testiMode) setTestiAsk({ field: tm[1], fmt });
      else insertTesti(testiMode, tm[1], fmt);
      return;
    }
    const real = key === "utente.firma" ? varValueMap[key] : fmtVar(varValueMap[key], fmt);
    if (key === "utente.firma" && !real) { toast.error("Nessuna firma grafica salvata: aggiungila da «Il mio profilo»"); return; }
    letEditorRef.current?.chain().focus().insertContent(real).run();
  };
  const copyVar = async (key, fmt) => {
    const token = "{{" + key + (fmt ? ":" + fmt : "") + "}}";
    try { await navigator.clipboard.writeText(token); toast.success(`Variabile ${token} copiata`); }
    catch { toast.error("Copia non riuscita"); }
  };
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewWithData = async (content) => {
    if (!pratica?.id) { setPreviewHtml(content || ""); return; }
    setPreviewLoading(true);
    setPreviewHtml("");
    try {
      const { data } = await api.post(`/studio/letter-templates/preview`, { pratica_id: pratica.id, content: content || "" });
      setPreviewHtml(data.content);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPreviewHtml(content || "");
    } finally { setPreviewLoading(false); }
  };

  const sedi = useMemo(() => sediList(studio), [studio]);
  const inte = useMemo(() => {
    if (sedeKey === "nessuna") return null;
    return sedi.find((x) => x.key === sedeKey) || sedi[0] || null;
  }, [sedi, sedeKey]);
  const withHead = sedeKey !== "nessuna" && !!inte;
  const pdfFooter = studio?.settings?.pdf_footer !== false;
  const changeSede = (v) => { setSedeKey(v); if (editing?.tplId) localStorage.setItem(`letter_sede_${editing.tplId}`, v); };

  const doDownloadPdf = () => {
    const clean = (s) => (s || "").trim().replace(/[^\p{L}\p{N}\-]+/gu, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
    const num = (pratica?.numero_pratica || "documento").replace(/\//g, "-");
    const tpl = (editing?.nome || "Documento").split("—")[0].trim();
    const parts = [clean(num), clean(pratica?.nome_assistito), clean(tpl)].filter(Boolean);
    const fileName = `${parts.join("_") || "documento"}.pdf`;
    toast.promise(downloadPdf(fileName, outContent(editing.content), withHead, inte, editorMargins, { footer: pdfFooter }), {
      loading: "Generazione PDF…", success: "PDF scaricato", error: "Generazione PDF non riuscita",
    });
  };

  const buildEditorPdfDoc = () => {
    return downloadPdf(editing?.nome || "lettera.pdf", outContent(editing?.content || ""), withHead, inte, editorMargins, { returnDoc: true, footer: pdfFooter });
  };

  const doPreview = async () => {
    const t = toast.loading("Generazione anteprima…");
    try {
      const doc = await buildEditorPdfDoc();
      setLetterPdfDoc(doc);
    } catch (e) { toast.error("Anteprima non riuscita"); }
    finally { toast.dismiss(t); }
  };

  const doPrint = async () => {
    const t = toast.loading("Preparazione stampa…");
    try {
      const doc = await buildEditorPdfDoc();
      const url = doc.output("bloburl");
      const w = window.open(url, "_blank");
      if (!w) toast.error("Consenti i popup per aprire il PDF");
    } catch (e) { toast.error("Generazione PDF non riuscita"); }
    finally { toast.dismiss(t); }
  };
  const lettereBase = `/studio/pratiche/${pratica?.id}/lettere`;

  const buildLetterPdfB64 = async () => {
    const ctx = emailCtx || { content: outContent(editing?.content || ""), margins: editorMargins, withHead };
    const content = ctx.content || "";
    const useHead = ctx.withHead ?? true;
    const letterId = emailCtx?.letterId ?? editing?.id ?? "corrente";
    // Riusa il PDF già generato per questa lettera se contenuto/intestazione/footer/margini non sono cambiati.
    const cacheKey = `${letterId}:${useHead}:${pdfFooter}:${inte?.align || "left"}:${JSON.stringify(globalMargins)}:${hashLetterKey(content)}`;
    try {
      return await getCachedLetterPdfB64(cacheKey, () =>
        downloadPdf("lettera.pdf", content, useHead, inte, globalMargins, { returnBase64: true, footer: pdfFooter }));
    } catch (e) {
      return null;
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [f, t, l, s] = await Promise.all([
        api.get("/studio/letter-folders"),
        api.get("/studio/letter-templates"),
      api.get(lettereBase),
        api.get("/studio/me"),
      ]);
      setFolders(f.data);
      setTemplates(t.data);
      setLetters(l.data);
      setStudio(s.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
    try {
      const url = pratica?.id ? `/studio/pratiche/${pratica.id}/variabili` : `/studio/letter-variables`;
      const { data } = await api.get(url);
      setVarCategories(normalizeCats(data.categories));
    } catch { /* pannello variabili non bloccante */ setVarCategories([]); }
  };

  useEffect(() => {
    if (open) {
      setTab("stampe"); setEditing(null); setTemplateEditing(null); setPreview(null); setShowCestino(false);
      setFilterTipo(pratica?.tipo && tipiPratica.some(([c]) => c === pratica.tipo) ? pratica.tipo : "__all__");
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assistitoKey]);

  // Alias tra codici tipo pratica (es. T-TRA) e i codici legacy usati nella tipizzazione
  // cartelle/template (es. TRASPORTATO). Così selezionando un tipo pratica si vedono anche
  // le cartelle tipizzate con il codice equivalente, oltre a quelle generiche.
  const TIPO_ALIASES = {
    "T-TRA": ["TRASPORTATO", "LESIONI TERZI TRASPORTATI"],
  };
  const aliasesFor = (code) => {
    if (!code || code === "__all__") return [];
    const up = String(code).toUpperCase();
    return [up, ...((TIPO_ALIASES[code] || TIPO_ALIASES[up] || []).map((x) => String(x).toUpperCase()))];
  };
  const tipoListMatches = (list) => {
    if (filterTipo === "__all__") return true;
    const al = aliasesFor(filterTipo);
    return (list || []).some((x) => al.includes(String(x).toUpperCase()));
  };

  const templateMatchesTipo = (t) => {
    if (filterTipo === "__all__") return true;
    const tp = t.tipi_pratica || [];
    return tp.length === 0 || tipoListMatches(tp);
  };
  const templatesOf = (folderId) => templates.filter((t) => (t.folder_id || null) === folderId && templateMatchesTipo(t));
  const unfiled = templates.filter((t) => !t.folder_id && templateMatchesTipo(t));
  const folderMatchesTipo = (f) => {
    if ((f.tipo_mode || "tutte") !== "specifiche") return true;
    if (filterTipo === "__all__") return true;
    return tipoListMatches(f.tipi);
  };
  const visibleFolders = [...folders.filter(folderMatchesTipo)].sort((a, b) => (b.preferita ? 1 : 0) - (a.preferita ? 1 : 0));
  const toggleFav = async (f) => {
    try {
      await api.patch(`/studio/letter-folders/${f.id}`, { preferita: !f.preferita });
      setFolders((fs) => fs.map((x) => (x.id === f.id ? { ...x, preferita: !f.preferita } : x)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const [folderEdit, setFolderEdit] = useState(null);
  const renameFolder = (f) => setFolderEdit({ id: f.id, name: f.name, colore: f.colore || "" });
  const saveFolderEdit = async () => {
    if (!folderEdit.name.trim()) { toast.error("Inserisci il nome cartella"); return; }
    try {
      await api.patch(`/studio/letter-folders/${folderEdit.id}`, { name: folderEdit.name.trim(), colore: folderEdit.colore || "" });
      toast.success("Cartella aggiornata");
      setFolders((fs) => fs.map((x) => (x.id === folderEdit.id ? { ...x, name: folderEdit.name.trim(), colore: folderEdit.colore || "" } : x)));
      setFolderEdit(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openTemplate = async (t) => {
    try {
      const { data } = await api.post(`/studio/letter-templates/${t.id}/generate`, { pratica_id: pratica.id });
      setEditing({ nome: `${data.title} — ${pratica.numero_pratica}`, content: data.content, margins: t.margins || globalMargins, tplId: t.id });
      setSedeKey(localStorage.getItem(`letter_sede_${t.id}`) || "principal");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const editTemplate = (t) => {
    setTemplateEditing({ id: t.id, title: t.title, content: t.content || "", folder_id: t.folder_id || null, margins: t.margins || globalMargins, tipi_pratica: t.tipi_pratica || [] });
  };

  const newTemplateInFolder = (folderId) => {
    setTemplateEditing({ id: null, title: "", content: "", folder_id: folderId || null, margins: globalMargins, tipi_pratica: [] });
  };

  const saveTemplate = async () => {
    if (!templateEditing.title.trim()) { toast.error("Inserisci un titolo per il template"); return; }
    if (editorOverflowsA4()) toast.warning(OVERFLOW_MSG);
    setSavingTpl(true);
    try {
      let savedId = templateEditing.id;
      if (templateEditing.id) {
        await api.patch(`/studio/letter-templates/${templateEditing.id}`, { title: templateEditing.title.trim(), content: templateEditing.content, margins: editorMargins, tipi_pratica: templateEditing.tipi_pratica || [] });
        toast.success("Template aggiornato");
      } else {
        const { data } = await api.post(`/studio/letter-templates`, { title: templateEditing.title.trim(), content: templateEditing.content, folder_id: templateEditing.folder_id, shared: true, margins: editorMargins, tipi_pratica: templateEditing.tipi_pratica || [] });
        savedId = data.id;
        toast.success("Template creato");
      }
      const { data } = await api.get("/studio/letter-templates");
      setTemplates(data);
      if (templateEditing.folder_id) setExpanded((e) => ({ ...e, [templateEditing.folder_id]: true }));
      setTemplateEditing((prev) => (prev ? { ...prev, id: savedId } : prev));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingTpl(false); }
  };

  const moveTemplate = async (t, folderId) => {
    try {
      await api.patch(`/studio/letter-templates/${t.id}`, { folder_id: folderId });
      const { data } = await api.get("/studio/letter-templates");
      setTemplates(data);
      if (folderId) setExpanded((e) => ({ ...e, [folderId]: true }));
      toast.success("Template spostato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyTemplate = async (t, folderId) => {
    try {
      await api.post(`/studio/letter-templates`, {
        title: `Copia di ${t.title}`,
        content: t.content || "",
        folder_id: folderId || null,
        shared: t.shared ?? true,
        margins: t.margins || null,
        tipi_pratica: t.tipi_pratica || [],
      });
      const { data } = await api.get("/studio/letter-templates");
      setTemplates(data);
      if (folderId) setExpanded((e) => ({ ...e, [folderId]: true }));
      toast.success("Template copiato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmFolderPick = (folderId) => {
    if (!folderPicker) return;
    if (folderPicker.mode === "copy") copyTemplate(folderPicker.t, folderId);
    else moveTemplate(folderPicker.t, folderId);
    setFolderPicker(null);
  };

  const delTemplate = async (t) => {
    if (!window.confirm(`Spostare il template "${t.title}" nel cestino?`)) return;
    try {
      await api.delete(`/studio/letter-templates/${t.id}`);
      setTemplates((x) => x.filter((i) => i.id !== t.id));
      toast.success("Template spostato nel cestino");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadCestino = async () => {
    try { const { data } = await api.get("/studio/letter-templates/cestino"); setCestinoTpls(data); }
    catch (e) { setCestinoTpls([]); }
  };

  const openCestino = () => { setShowCestino(true); loadCestino(); };

  const restoreTemplate = async (t) => {
    try {
      await api.post(`/studio/letter-templates/${t.id}/restore`);
      const { data } = await api.get("/studio/letter-templates");
      setTemplates(data);
      setCestinoTpls((x) => x.filter((i) => i.id !== t.id));
      if (t.folder_id) setExpanded((e) => ({ ...e, [t.folder_id]: true }));
      toast.success("Template ripristinato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const permanentDeleteTemplate = async (t) => {
    if (!window.confirm(`Eliminare DEFINITIVAMENTE il template "${t.title}"? L'operazione è irreversibile.`)) return;
    try {
      await api.delete(`/studio/letter-templates/${t.id}/permanent`);
      setCestinoTpls((x) => x.filter((i) => i.id !== t.id));
      toast.success("Template eliminato definitivamente");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const editLetter = (l) => {
    setEditing({ id: l.id, nome: l.nome, content: l.content || "", margins: l.margins || globalMargins, template_id: l.template_id || null });
    setSedeKey(l.sede_key || "principal");
  };

  const duplicateLetter = (l) => {
    setEditing({ nome: `Copia di ${l.nome}`, content: l.content || "", margins: l.margins || globalMargins });
    setSedeKey("principal");
  };

  const restoreVersion = async (v) => {
    try {
      await api.patch(`${lettereBase}/${versionsLetter.id}`, { nome: v.nome, content: v.content });
      const { data } = await api.get(lettereBase);
      setLetters(data);
      setVersionsLetter(null);
      toast.success("Versione ripristinata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveLetter = async () => {
    if (!editing.nome.trim()) { toast.error("Inserisci un nome file"); return; }
    if (editorOverflowsA4()) toast.warning(OVERFLOW_MSG);
    if (testiMode === "each" && /\{\{testimone\.[\w.]+(?::\w+)?\}\}/.test(editing.content || "")) { await saveLettersPerTestimone(); return; }
    // Lettera già esistente (aperta in modifica): chiedi se sostituire o salvarne una copia,
    // per evitare duplicati involontari.
    if (editing.id) { setSaveChoice({ renaming: false, newName: `Copia di ${editing.nome.trim()}` }); return; }
    await persistNewLetter(editing.nome.trim());
  };

  // Una lettera per ciascun testimone, ognuna con i rispettivi dati.
  const saveLettersPerTestimone = async () => {
    setSaving(true);
    try {
      for (const i of testiIdx) {
        const who = varValueMap[`testimone_${i}.nominativo`] || [varValueMap[`testimone_${i}.cognome`], varValueMap[`testimone_${i}.nome`]].filter(Boolean).join(" ") || `Testimone ${i}`;
        await api.post(lettereBase, { nome: `${editing.nome.trim()} — ${who}`, content: resolveTesti(editing.content, i), margins: editorMargins, assistito_key: assistitoKey, sede_key: sedeKey, template_id: editing.tplId || editing.template_id || null });
      }
      toast.success(`Create ${testiIdx.length} lettere, una per ciascun testimone`);
      const { data } = await api.get(lettereBase);
      setLetters(data);
      flashSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // Sovrascrive la lettera originale (nessun duplicato).
  const replaceLetter = async () => {
    setSaving(true);
    try {
      await api.patch(`${lettereBase}/${editing.id}`, { nome: editing.nome.trim(), content: editing.content, margins: editorMargins, sede_key: sedeKey });
      toast.success("Lettera aggiornata nella pratica");
      const { data } = await api.get(lettereBase);
      setLetters(data);
      setSaveChoice(null);
      flashSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // Crea una nuova lettera (copia) con il nome indicato.
  const persistNewLetter = async (nome) => {
    if (!nome || !nome.trim()) { toast.error("Inserisci un nome file"); return; }
    setSaving(true);
    try {
      const { data: created } = await api.post(lettereBase, { nome: nome.trim(), content: editing.content, margins: editorMargins, assistito_key: assistitoKey, sede_key: sedeKey, template_id: editing.tplId || editing.template_id || null });
      toast.success("Lettera salvata nella pratica");
      const { data } = await api.get(lettereBase);
      setLetters(data);
      setSaveChoice(null);
      const newId = created?.id || (data || []).find((l) => l.nome === nome.trim())?.id;
      setEditing((ed) => (ed ? { ...ed, id: newId || ed.id, nome: nome.trim() } : ed));
      flashSaved();
      return newId;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // Se la lettera non è ancora salvata, la salva prima dell'invio così la nota di invio resta agganciata.
  const openEditorEmail = async () => {
    let lid = editing.id;
    if (!lid) {
      lid = await persistNewLetter(editing.nome || "");
      if (!lid) return;
    }
    setEmailCtx({ nome: editing.nome.trim(), content: outContent(editing.content), margins: editorMargins, withHead, emailHead: withHead ? inte : null, letterId: lid });
    setEmailOpen(true);
  };

  const openFirmaRemota = async () => {
    let lid = editing.id;
    if (!lid) {
      lid = await persistNewLetter(editing.nome || "");
      if (!lid) return;
    }
    const content = outContent(editing.content);
    setFirmaCtx({ id: lid, nome: (editing.nome || "Mandato").trim(),
      buildPdf: () => downloadPdf("mandato.pdf", content, withHead, inte, editorMargins, { returnBase64: true, footer: pdfFooter, marker: "Firma del Cliente" }) });
  };
  const [savedFlash, setSavedFlash] = useState(false);
  const flashSaved = () => { setSavedFlash(true); setTimeout(() => setSavedFlash(false), 4000); };

  const delLetter = async (l) => {
    if (!window.confirm(`Eliminare "${l.nome}"?`)) return;
    try { await api.delete(`${lettereBase}/${l.id}`); setLetters((x) => x.filter((i) => i.id !== l.id)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const emailLetter = (l) => {
    const sk = l.sede_key || "";
    setEmailCtx({ nome: l.nome, content: l.content || "", margins: globalMargins, withHead: true, letterId: l.id,
      emailHead: sk && sk !== "nessuna" ? (sedi.find((x) => x.key === sk) || sedi[0] || null) : null });
    setEmailOpen(true);
  };

  const openLetterPreview = async (l) => {
    setPreview(l); setPreviewHead("con"); setPreviewRendered(null);
    if (!pratica?.id) { setPreviewRendered(l.content || ""); return; }
    setPreviewRendering(true);
    try {
      const { data } = await api.post(`/studio/letter-templates/preview`, { pratica_id: pratica.id, content: l.content || "" });
      setPreviewRendered(data.content);
    } catch (e) {
      setPreviewRendered(l.content || "");
    } finally { setPreviewRendering(false); }
  };

  const handleMainOpenChange = (o) => {
    if (!o && templateEditing) { setTemplateEditing(null); return; }
    onOpenChange(o);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleMainOpenChange}>
      <DialogContent className="pgp-modal-full bg-[#121824] border-slate-700 text-slate-100 max-w-none h-[90vh] overflow-hidden flex flex-col" data-testid="gestione-lettere-dialog"
        onEscapeKeyDown={(e) => { if (letterPdfDoc) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (letterPdfDoc || e.detail?.originalEvent?.target?.closest?.("[data-testid=copertina-preview]")) e.preventDefault(); }}
        onInteractOutside={(e) => { if (letterPdfDoc) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-400" /> Gestione Lettere
            <span className="text-sm font-normal text-slate-500">· {pratica?.numero_pratica} {assistitoName || pratica?.nome_assistito}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-800 -mt-1">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
              className={`px-3 py-2 text-xs font-semibold tracking-wide border-b-2 -mb-px transition-colors ${
                tab === t.k ? "border-amber-500 text-amber-400" : "border-transparent text-slate-400 hover:text-slate-200"
              }`}>
              {t.l}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto pt-4">
          {tab === "impostazioni" ? (
            <div className="pgp-card p-6 max-w-2xl" data-testid="impostazioni-stampe-tab">
              <h3 className="font-head font-semibold text-slate-100">Impostazioni Stampe</h3>
              <p className="text-sm text-slate-400 mt-1">Margini pagina applicati come default a tutti i PDF del gestionale (lettere, copertina pratica). Nell'editor lettere puoi sovrascriverli temporaneamente con il controllo "MARGINI MM".</p>
              <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-8 items-start">
                <div className="grid grid-cols-2 gap-4">
                  {[["top", "Margine superiore"], ["bottom", "Margine inferiore"], ["left", "Margine sinistro"], ["right", "Margine destro"]].map(([k, label]) => (
                    <div key={k}>
                      <Label className="text-slate-300 text-sm">{label} (mm)</Label>
                      <Input type="number" min="0" max="60" value={pm[k]} onChange={(e) => setPm(k, Number(e.target.value) || 0)} data-testid={`gl-print-margin-${k}`} className="mt-1.5 bg-slate-900/60 border-slate-700" />
                    </div>
                  ))}
                  <div className="col-span-2">
                    <Button type="button" onClick={savePrintMargins} disabled={savingPm} data-testid="gl-print-margins-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                      {savingPm ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva
                    </Button>
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className="relative bg-white border border-slate-500 shadow-lg" style={{ width: "210px", height: "297px" }} data-testid="gl-print-margins-preview">
                    <div className="absolute border-2 border-dashed border-amber-500 bg-amber-500/10" style={{ top: `${pm.top}px`, bottom: `${pm.bottom}px`, left: `${pm.left}px`, right: `${pm.right}px` }} />
                    <span className="absolute left-1/2 -translate-x-1/2 text-[10px] font-mono text-slate-600" style={{ top: "3px" }}>{pm.top} mm</span>
                    <span className="absolute left-1/2 -translate-x-1/2 text-[10px] font-mono text-slate-600" style={{ bottom: "3px" }}>{pm.bottom} mm</span>
                    <span className="absolute top-1/2 -translate-y-1/2 text-[10px] font-mono text-slate-600" style={{ left: "3px", writingMode: "vertical-rl" }}>{pm.left} mm</span>
                    <span className="absolute top-1/2 -translate-y-1/2 text-[10px] font-mono text-slate-600" style={{ right: "3px", writingMode: "vertical-rl" }}>{pm.right} mm</span>
                  </div>
                </div>
              </div>
            </div>
          ) : tab !== "stampe" ? (
            <TabPanel title={TABS.find((t) => t.k === tab).l}>
              {tab === "mail" && "Configurazione dell'account mail generale dello studio per l'invio delle lettere. In arrivo."}
              {tab === "firmati" && "Archivio dei modelli firmati digitalmente riutilizzabili. In arrivo."}
            </TabPanel>
          ) : editing ? (
            /* EDITOR */
            <div data-testid="lettera-editor">
              <div className="flex flex-wrap items-end gap-2 mb-2">
                <button onClick={() => setEditing(null)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-amber-400">
                  <ArrowLeft className="h-3.5 w-3.5" /> Indietro
                </button>
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-slate-300 text-[10px]">Nome file</Label>
                  <Input value={editing.nome} onChange={(e) => setEditing({ ...editing, nome: e.target.value })} data-testid="lettera-nome" className="mt-0.5 h-8 text-sm bg-slate-900/60 border-slate-700" />
                </div>
                <Select value={sedeKey} onValueChange={changeSede}>
                  <SelectTrigger data-testid="editor-letterhead" className="w-48 h-8 text-sm bg-slate-900/60 border-slate-700"><SelectValue placeholder="Sede Intestazione Stampa" /></SelectTrigger>
                  <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                    {sedi.map((s) => (
                      <SelectItem key={s.key} value={s.key} data-testid={`sede-opt-${s.key}`}>{s.label}</SelectItem>
                    ))}
                    <SelectItem value="nessuna" data-testid="sede-opt-nessuna">Nessuna intestazione</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={doPreview} data-testid="editor-preview" variant="outline" size="sm" className="h-8 text-xs border-slate-700 text-slate-200 hover:bg-slate-800">
                  <Eye className="h-3.5 w-3.5 mr-1" /> Anteprima
                </Button>
                <Button onClick={doPrint} data-testid="editor-print-btn" variant="outline" size="sm" className="h-8 text-xs border-slate-700 text-slate-200 hover:bg-slate-800">
                  <Printer className="h-3.5 w-3.5 mr-1" /> Stampa
                </Button>
                <Button onClick={doDownloadPdf} data-testid="editor-print" size="sm" className="h-8 text-xs bg-cyan-600 hover:bg-cyan-700 text-white">
                  <Download className="h-3.5 w-3.5 mr-1" /> Scarica PDF
                </Button>
                <Button onClick={openEditorEmail} disabled={saving} data-testid="editor-email" size="sm" className="h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white">
                  <Mail className="h-3.5 w-3.5 mr-1" /> Invia via Email
                </Button>
                {pratica?.id && (
                  <Button onClick={openFirmaRemota} disabled={saving} data-testid="editor-firma-remota" size="sm" className="h-8 text-xs bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                    <PenLine className="h-3.5 w-3.5 mr-1" /> Firma a distanza
                  </Button>
                )}
                {savedFlash && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 px-2 h-8 text-xs font-semibold text-emerald-400" data-testid="editor-saved-banner">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Lettera salvata nella pratica
                  </span>
                )}
                <Button onClick={saveLetter} disabled={saving} data-testid="editor-save" size="sm" className="h-8 text-xs bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" /> Salva nella pratica</>}
                </Button>
                <Dialog open={!!saveChoice} onOpenChange={(o) => { if (!o && !saving) setSaveChoice(null); }}>
                  <DialogContent data-testid="letter-save-choice-dialog" className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Vuoi sostituire la lettera esistente o salvarne una copia?</DialogTitle>
                    </DialogHeader>
                    {saveChoice && !saveChoice.renaming && (
                      <div className="flex flex-col gap-3 pt-2">
                        <p className="text-sm text-slate-400">La lettera "{editing?.nome}" esiste già. Scegli come salvare le modifiche.</p>
                        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                          <Button onClick={() => setSaveChoice((s) => ({ ...s, renaming: true }))} disabled={saving} variant="outline" data-testid="letter-save-as-new" className="h-9 text-sm border-slate-600 text-slate-200 hover:bg-slate-800">
                            Salva come nuova
                          </Button>
                          <Button onClick={replaceLetter} disabled={saving} data-testid="letter-save-replace" className="h-9 text-sm bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sostituisci"}
                          </Button>
                        </div>
                      </div>
                    )}
                    {saveChoice && saveChoice.renaming && (
                      <div className="flex flex-col gap-3 pt-2">
                        <div>
                          <Label className="text-slate-300 text-xs">Nome della nuova lettera</Label>
                          <Input autoFocus value={saveChoice.newName} onChange={(e) => setSaveChoice((s) => ({ ...s, newName: e.target.value }))}
                            data-testid="letter-new-name" className="mt-1 h-9 text-sm bg-slate-900/60 border-slate-700"
                            onKeyDown={(e) => { if (e.key === "Enter") persistNewLetter(saveChoice.newName); }} />
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                          <Button onClick={() => setSaveChoice((s) => ({ ...s, renaming: false }))} disabled={saving} variant="outline" data-testid="letter-new-name-back" className="h-9 text-sm border-slate-600 text-slate-200 hover:bg-slate-800">
                            Indietro
                          </Button>
                          <Button onClick={() => persistNewLetter(saveChoice.newName)} disabled={saving || !saveChoice.newName.trim()} data-testid="letter-new-name-save" className="h-9 text-sm bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva copia"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
              <div className="flex gap-4">
                <ResizableVarsPanel storageKey="pgp_letter_vars_w" testId="lettera-vars-panel" className="max-h-[52vh] overflow-y-auto pgp-card p-3 space-y-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono font-bold mb-1">Variabili</div>
                  <p className="text-[10px] text-slate-500 mb-2 leading-snug">Apri una categoria e clicca un formato per inserire la variabile nel punto del cursore.</p>
                  {varCategories.map((cat) => {
                    const open = !!varOpen["let:" + cat.title];
                    return (
                      <div key={cat.title} className="rounded-lg border border-border bg-card overflow-hidden shadow-sm">
                        <button type="button" onClick={() => toggleCat("let:" + cat.title)} data-testid={`let-cat-${cat.title}`}
                          className="w-full flex items-center gap-2 px-3 py-2.5 text-left bg-muted hover:bg-accent transition-colors">
                          {open ? <FolderOpen className="h-4 w-4 text-amber-400 shrink-0" /> : <Folder className="h-4 w-4 text-amber-400/70 shrink-0" />}
                          <span className="text-[13px] font-semibold text-foreground flex-1 truncate">{cat.title}</span>
                          <span className="text-[9px] text-slate-500">{(cat.vars || []).length}</span>
                          {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-500 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500 shrink-0" />}
                        </button>
                        {open && (
                          <div className="px-2 py-2 space-y-1.5 border-t border-border bg-popover">
                            {(cat.vars || []).map((v) => (
                              <div key={v.key + cat.title} className="flex items-start justify-between gap-1" data-testid={`let-var-row-${v.key}`}>
                                <div className="min-w-0 flex-1">
                                  <div className="text-[13px] text-foreground font-medium truncate" title={v.label}>{v.label}</div>
                                  {v.value ? (
                                    <div className="text-[12px] text-amber-600 font-mono truncate" title={v.value} data-testid={`let-var-preview-${v.key}`}>{v.value}</div>
                                  ) : null}
                                </div>
                                <div className="flex gap-0.5 shrink-0 pt-0.5">
                                  <button type="button" onClick={() => insertVar(v.key, "upper")} data-testid={`let-var-${v.key}-upper`} title="TUTTO MAIUSCOLO" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">AA</button>
                                  <button type="button" onClick={() => insertVar(v.key, "title")} data-testid={`let-var-${v.key}-title`} title="Prima lettera maiuscola" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">Aa</button>
                                  <button type="button" onClick={() => insertVar(v.key, "lower")} data-testid={`let-var-${v.key}-lower`} title="tutto minuscolo" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">aa</button>
                                  <button type="button" onClick={() => insertVar(v.key, "titlecase")} data-testid={`let-var-${v.key}-titlecase`} title="Nome e Cognome (Title Case)" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">Nc</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </ResizableVarsPanel>
                <div className="flex-1 min-w-0">
                  {testiMode === "each" && (
                    <div className="mb-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-300" data-testid="testi-each-banner">
                      Verranno create {testiIdx.length} lettere, una per ciascun testimone. Anteprima, stampa, PDF ed email usano i dati del Testimone {testiIdx[0]}.
                    </div>
                  )}
                  <div className="flex items-start gap-3 flex-wrap">
                    <FirmaPicker editorRef={letEditorRef} />
                    <AiLetterButton editorRef={letEditorRef} praticaId={pratica?.id} tipoLettera={editing?.nome} templateId={editing?.tplId} />
                  </div>
                  <RichTextEditor value={editing.content} onChange={(html) => setEditing((e) => ({ ...e, content: html }))} onEditorReady={(ed) => { letEditorRef.current = ed; }} onMarginsChange={setEditorMargins} initialMargins={editing.margins || globalMargins} withHead={withHead} letterhead={inte} destinatariCompagnie={destinatariCompagnie} />
                </div>
                <VarsCatalogPanel varCategories={varCategories} onPick={insertVar} insertMode open={catalogOpen} onToggle={toggleCatalog} />
              </div>
            </div>
          ) : templateEditing ? (
            /* TEMPLATE EDITOR */
            <div data-testid="template-editor">
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <button onClick={() => setTemplateEditing(null)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-amber-400">
                  <ArrowLeft className="h-4 w-4" /> Indietro
                </button>
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-slate-300 text-xs">Titolo template</Label>
                  <Input value={templateEditing.title} onChange={(e) => setTemplateEditing({ ...templateEditing, title: e.target.value })} data-testid="template-title" className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
                </div>
                <TipiPraticaSelect value={templateEditing.tipi_pratica} onChange={(v) => setTemplateEditing((e) => ({ ...e, tipi_pratica: v }))} tipi={tipiPratica} />
                <Button onClick={() => previewWithData(templateEditing.content)} data-testid="template-preview" variant="outline" className="h-9 border-slate-700 text-slate-200 hover:bg-slate-800">
                  <Eye className="h-4 w-4 mr-1" /> Anteprima dati reali
                </Button>
                <Button onClick={saveTemplate} disabled={savingTpl} data-testid="template-save" className="h-9 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                  {savingTpl ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Salva template</>}
                </Button>
              </div>
              <div className="flex gap-4">
                <ResizableVarsPanel storageKey="pgp_tpl_vars_w" testId="template-vars-panel" className="max-h-[52vh] overflow-y-auto pgp-card p-3 space-y-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono font-bold mb-1">Variabili</div>
                  <p className="text-[10px] text-slate-500 mb-2 leading-snug">Apri una categoria e clicca un formato per inserire la variabile nel punto del cursore.</p>
                  {varCategories.map((cat) => {
                    const open = !!varOpen["tpl:" + cat.title];
                    return (
                      <div key={cat.title} className="rounded-lg border border-border bg-card overflow-hidden shadow-sm">
                        <button type="button" onClick={() => toggleCat("tpl:" + cat.title)} data-testid={`tpl-cat-${cat.title}`}
                          className="w-full flex items-center gap-2 px-3 py-2.5 text-left bg-muted hover:bg-accent transition-colors">
                          {open ? <FolderOpen className="h-4 w-4 text-amber-400 shrink-0" /> : <Folder className="h-4 w-4 text-amber-400/70 shrink-0" />}
                          <span className="text-[13px] font-semibold text-foreground flex-1 truncate">{cat.title}</span>
                          <span className="text-[9px] text-slate-500">{(cat.vars || []).length}</span>
                          {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-500 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500 shrink-0" />}
                        </button>
                        {open && (
                          <div className="px-2 py-2 space-y-1.5 border-t border-border bg-popover">
                            {(cat.vars || []).map((v) => (
                              <div key={v.key + cat.title} className="flex items-start justify-between gap-1" data-testid={`var-row-${v.key}`}>
                                <div className="min-w-0 flex-1">
                                  <div className="text-[13px] text-foreground font-medium truncate" title={v.label}>{v.label}</div>
                                  {v.value ? (
                                    <div className="text-[12px] text-amber-600 font-mono truncate" title={v.value} data-testid={`var-preview-${v.key}`}>{v.value}</div>
                                  ) : null}
                                </div>
                                <div className="flex gap-0.5 shrink-0 pt-0.5">
                                  <button type="button" onClick={() => insertVar(v.key, "upper")} data-testid={`var-${v.key}-upper`} title="TUTTO MAIUSCOLO" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">AA</button>
                                  <button type="button" onClick={() => insertVar(v.key, "title")} data-testid={`var-${v.key}-title`} title="Prima lettera maiuscola" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">Aa</button>
                                  <button type="button" onClick={() => insertVar(v.key, "lower")} data-testid={`var-${v.key}-lower`} title="tutto minuscolo" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">aa</button>
                                  <button type="button" onClick={() => insertVar(v.key, "titlecase")} data-testid={`var-${v.key}-titlecase`} title="Nome e Cognome (Title Case)" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">Nc</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </ResizableVarsPanel>
                <div className="flex-1 min-w-0">
                  <div className="mb-1.5 flex justify-end"><TemplateAIButton editorRef={tplEditorRef} titolo={templateEditing.title} testId="gl-tpl-ai" /></div>
                  <RichTextEditor value={templateEditing.content} onChange={(html) => setTemplateEditing((e) => ({ ...e, content: html }))} onEditorReady={(ed) => { tplEditorRef.current = ed; }} onMarginsChange={setEditorMargins} initialMargins={templateEditing.margins || globalMargins} />
                </div>
              </div>
            </div>
          ) : showCestino ? (
            /* CESTINO TEMPLATE */
            <div data-testid="template-cestino">
              <div className="flex items-center gap-3 mb-4">
                <button onClick={() => setShowCestino(false)} data-testid="cestino-back" className="flex items-center gap-1 text-sm text-slate-400 hover:text-amber-400">
                  <ArrowLeft className="h-4 w-4" /> Indietro
                </button>
                <div className="text-xs uppercase tracking-widest text-amber-400 font-mono font-bold flex items-center gap-2">
                  <Trash2 className="h-4 w-4" /> Cestino template
                </div>
              </div>
              {cestinoTpls.length === 0 ? (
                <div className="pgp-card p-8 text-center text-sm text-slate-500" data-testid="cestino-empty">Il cestino è vuoto.</div>
              ) : (
                <div className="pgp-card divide-y divide-slate-800">
                  {cestinoTpls.map((t) => (
                    <div key={t.id} data-testid={`cestino-row-${t.id}`} className="flex items-center gap-3 px-3 py-3">
                      <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-200 truncate">{t.title}</div>
                        <div className="text-[11px] text-slate-500 font-mono">Cartella: {folders.find((f) => f.id === t.folder_id)?.name || "Senza cartella"}{t.cestino_at ? ` · eliminato il ${formatDate(t.cestino_at)}` : ""}</div>
                      </div>
                      <button onClick={() => restoreTemplate(t)} data-testid={`cestino-restore-${t.id}`} title="Ripristina nella cartella originale" className="flex items-center gap-1 text-xs rounded-md border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 px-2 py-1 shrink-0 transition-colors">
                        <RotateCcw className="h-3.5 w-3.5" /> Ripristina
                      </button>
                      <button onClick={() => permanentDeleteTemplate(t)} data-testid={`cestino-del-${t.id}`} title="Elimina definitivamente" className="p-1 rounded hover:bg-slate-700/50 text-red-400 hover:text-red-300 shrink-0">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* TWO COLUMNS + VARIABLES PANEL */
            <div className="flex gap-4">
              <div className="flex-1 min-w-0 flex">
              {/* LEFT: folders/templates */}
              <div className="min-w-0" style={{ width: leftWidth, flex: "0 0 auto" }}>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <div className="text-xs uppercase tracking-widest text-amber-400 font-mono font-bold min-w-0 truncate flex-1">
                    STAMPE PER LA PRATICA DEL DANNEGGIATO
                  </div>
                  <Select value={filterTipo} onValueChange={setFilterTipo}>
                    <SelectTrigger data-testid="gl-filter-tipo" className="w-52 h-8 bg-slate-900/60 border-slate-700 text-xs shrink-0">
                      <Filter className="h-3.5 w-3.5 mr-1 text-amber-400 shrink-0" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121824] border-slate-700 text-slate-100 max-h-72">
                      <SelectItem value="__all__">Tutti i tipi</SelectItem>
                      {tipiPratica.map(([code, label]) => (
                        <SelectItem key={code} value={code} data-testid={`gl-filter-tipo-${code}`}>{code} · {label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button onClick={openCestino} data-testid="gl-open-cestino" title="Cestino template" className="flex items-center gap-1 text-xs rounded-md border border-slate-700 text-slate-300 hover:text-amber-400 hover:border-amber-500/40 px-2 h-8 shrink-0 transition-colors">
                    <Trash2 className="h-3.5 w-3.5" /> Cestino
                  </button>
                </div>
                {loading ? (
                  <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
                ) : (
                  <div className="pgp-card divide-y divide-slate-800">
                    {visibleFolders.map((f, idx) => {
                      const tpls = templatesOf(f.id);
                      const isOpen = expanded[f.id];
                      const fStyle = f.colore ? { color: f.colore } : undefined;
                      return (
                        <div key={f.id} data-testid={`folder-${f.id}`}>
                          {f.preferita && idx === 0 && <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-amber-600 font-mono" data-testid="gl-folders-section-preferite"><Star className="h-3 w-3 fill-amber-400 text-amber-500" /> Preferite</div>}
                          {!f.preferita && idx > 0 && visibleFolders[idx - 1].preferita && <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-mono" data-testid="gl-folders-section-altre">Altre cartelle</div>}
                          <div className="flex items-center gap-2 px-3 py-2.5 hover:bg-slate-800/40"
                            style={f.colore ? { backgroundColor: `${f.colore}2e`, borderLeft: `4px solid ${f.colore}` } : undefined} data-testid={`gl-folder-row-${f.id}`}>
                            <button onClick={() => toggleFav(f)} data-testid={`gl-fav-folder-${f.id}`} title={f.preferita ? "Rimuovi dalle preferite" : "Segna come preferita"} className="shrink-0 transition-transform hover:scale-110">
                              <Star className={`h-3.5 w-3.5 ${f.preferita ? "fill-amber-400 text-amber-500" : "text-slate-400 hover:text-amber-500"}`} />
                            </button>
                            <button onClick={() => toggle(f.id)} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                              {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />}
                              {isOpen ? <FolderOpen className="h-4 w-4 text-amber-400 shrink-0" style={fStyle} /> : <Folder className="h-4 w-4 text-amber-400 shrink-0" style={fStyle} />}
                              <span className="text-sm text-slate-200 truncate">{f.name}</span>
                              <span className="text-[10px] text-slate-500 font-mono">{tpls.length}</span>
                            </button>
                            <button onClick={() => renameFolder(f)} data-testid={`rename-folder-${f.id}`} className="text-slate-300 hover:text-amber-400 shrink-0">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => newTemplateInFolder(f.id)} data-testid={`folder-new-${f.id}`} title="Nuovo template nella cartella" className="text-slate-300 hover:text-emerald-400 shrink-0">
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          {isOpen && (
                            <div className="bg-slate-900/30">
                              {tpls.length === 0 ? (
                                <div className="pl-11 pr-3 py-2 text-xs text-slate-500">Nessun template in questa cartella.</div>
                              ) : tpls.map((t) => (
                                <div key={t.id} data-testid={`template-row-${t.id}`} className="w-full flex items-center gap-1.5 pl-11 pr-3 py-2 hover:bg-slate-800/50">
                                  <button onClick={() => openTemplate(t)} data-testid={`template-${t.id}`} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                                    <FileText className="h-3.5 w-3.5 text-cyan-400 shrink-0" />
                                    <span className="text-sm text-slate-300 truncate">{t.title}</span>
                                  </button>
                                  <button onClick={() => editTemplate(t)} data-testid={`template-edit-${t.id}`} title="Modifica template" className="p-1 rounded hover:bg-slate-700/50 text-slate-300 hover:text-amber-400 shrink-0"><Pencil className="h-4 w-4" /></button>
                                  <button onClick={() => newTemplateInFolder(t.folder_id || null)} data-testid={`template-new-${t.id}`} title="Nuovo template nella cartella" className="p-1 rounded hover:bg-slate-700/50 text-slate-300 hover:text-emerald-400 shrink-0"><Plus className="h-4 w-4" /></button>
                                  <button onClick={() => setFolderPicker({ t, mode: "copy" })} data-testid={`template-copy-${t.id}`} title="Copia in una cartella…" className="p-1 rounded hover:bg-slate-700/50 text-slate-300 hover:text-emerald-400 shrink-0"><Copy className="h-4 w-4" /></button>
                                  <button onClick={() => setFolderPicker({ t, mode: "move" })} data-testid={`template-move-${t.id}`} title="Sposta in cartella…" className="p-1 rounded hover:bg-slate-700/50 text-slate-300 hover:text-cyan-400 shrink-0"><FolderInput className="h-4 w-4" /></button>
                                  <button onClick={() => delTemplate(t)} data-testid={`template-del-${t.id}`} title="Elimina template" className="p-1 rounded hover:bg-slate-700/50 text-red-400 hover:text-red-300 shrink-0"><Trash2 className="h-4 w-4" /></button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {unfiled.length > 0 && (
                      <div>
                        <div className="px-3 py-2.5 text-xs text-slate-500 font-mono">Senza cartella</div>
                        {unfiled.map((t) => (
                          <div key={t.id} data-testid={`template-row-${t.id}`} className="w-full flex items-center gap-1.5 pl-11 pr-3 py-2 hover:bg-slate-800/50">
                            <button onClick={() => openTemplate(t)} data-testid={`template-${t.id}`} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                              <FileText className="h-3.5 w-3.5 text-cyan-400 shrink-0" />
                              <span className="text-sm text-slate-300 truncate">{t.title}</span>
                            </button>
                            <button onClick={() => editTemplate(t)} data-testid={`template-edit-${t.id}`} title="Modifica template" className="p-1 rounded hover:bg-slate-700/50 text-slate-300 hover:text-amber-400 shrink-0"><Pencil className="h-4 w-4" /></button>
                            <button onClick={() => newTemplateInFolder(t.folder_id || null)} data-testid={`template-new-${t.id}`} title="Nuovo template nella cartella" className="p-1 rounded hover:bg-slate-700/50 text-slate-300 hover:text-emerald-400 shrink-0"><Plus className="h-4 w-4" /></button>
                            <button onClick={() => setFolderPicker({ t, mode: "copy" })} data-testid={`template-copy-${t.id}`} title="Copia in una cartella…" className="p-1 rounded hover:bg-slate-700/50 text-slate-300 hover:text-emerald-400 shrink-0"><Copy className="h-4 w-4" /></button>
                            <button onClick={() => setFolderPicker({ t, mode: "move" })} data-testid={`template-move-${t.id}`} title="Sposta in cartella…" className="p-1 rounded hover:bg-slate-700/50 text-slate-300 hover:text-cyan-400 shrink-0"><FolderInput className="h-4 w-4" /></button>
                            <button onClick={() => delTemplate(t)} data-testid={`template-del-${t.id}`} title="Elimina template" className="p-1 rounded hover:bg-slate-700/50 text-red-400 hover:text-red-300 shrink-0"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* RESIZE HANDLE */}
              <div onMouseDown={startResize} data-testid="gl-folders-resize" title="Trascina per ridimensionare il pannello cartelle"
                className="w-1.5 mx-1.5 cursor-col-resize shrink-0 rounded bg-slate-700/40 hover:bg-amber-500/60 transition-colors" />

              {/* RIGHT: saved letters */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center h-8 text-xs uppercase tracking-widest text-cyan-400 font-mono font-bold mb-3">ESPLORA FILE</div>
                <div className="pgp-card divide-y divide-slate-800 min-h-[200px] mt-2">
                  {letters.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-500">Nessuna lettera salvata per questa pratica.</div>
                  ) : letters.map((l) => (
                    <div key={l.id} className="flex items-center gap-3 px-3 py-1" data-testid={`lettera-${l.id}`}>
                      <FileType2 className="h-4 w-4 text-red-400 shrink-0" />
                      <button onClick={() => editLetter(l)} data-testid={`lettera-open-${l.id}`} title="Apri lettera" className="flex-1 min-w-0 text-left group">
                        <div className="flex items-baseline gap-2">
                          <div className="text-sm text-slate-100 truncate group-hover:text-amber-400 transition-colors">{l.nome}</div>
                          <div className="text-[11px] text-slate-400 font-mono shrink-0" data-testid={`lettera-created-${l.id}`}>{formatDateTime(l.updated_at || l.created_at)}</div>
                        </div>
                        {Array.isArray(l.sends) && l.sends.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`lettera-sends-${l.id}`}>
                            {l.sends.map((s, i) => (
                              <span key={i} data-testid={`lettera-send-badge-${l.id}-${i}`}
                                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${s.via === "pec" ? "bg-violet-500/15 text-violet-300 border-violet-500/40" : "bg-sky-500/15 text-sky-300 border-sky-500/40"}`}>
                                {s.via === "pec" ? "PEC" : "Email"} · {formatDateTimeSec(s.at)}
                                {[...(s.to || []), ...(s.cc || []), ...(s.bcc || [])].length > 0 && (
                                  <span className="break-all" data-testid={`lettera-send-rcpt-${l.id}-${i}`}> · {[...(s.to || []), ...(s.cc || []), ...(s.bcc || [])].join(", ")}</span>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                      </button>
                      <button onClick={() => editLetter(l)} data-testid={`lettera-edit-${l.id}`} title="Modifica" className="text-slate-300 hover:text-amber-400"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => duplicateLetter(l)} data-testid={`lettera-duplica-${l.id}`} title="Duplica" className="text-slate-300 hover:text-emerald-400"><Copy className="h-4 w-4" /></button>
                      <button onClick={() => setVersionsLetter(l)} data-testid={`lettera-storico-${l.id}`} title="Storico versioni" className="text-slate-300 hover:text-violet-400"><History className="h-4 w-4" /></button>
                      <button onClick={() => delLetter(l)} data-testid={`lettera-del-${l.id}`} title="Elimina" className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                      <button onClick={() => emailLetter(l)} data-testid={`lettera-mail-${l.id}`} title="Invia via email" className="text-slate-300 hover:text-amber-400"><Mail className="h-4 w-4" /></button>
                      <button onClick={() => openLetterPreview(l)} data-testid={`lettera-print-${l.id}`} title="Anteprima / stampa" className="text-slate-300 hover:text-cyan-400"><Printer className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
              </div>
              {/* RIGHT SIDE: variables catalog */}
              <VarsCatalogPanel varCategories={varCategories} onPick={copyVar} open={catalogOpen} onToggle={toggleCatalog} />
            </div>
          )}
        </div>
      </DialogContent>
      </Dialog>

      {/* Preview / print saved letter */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { setPreview(null); setPreviewRendered(null); setPreviewRendering(false); } }}>
        <DialogContent className="pgp-modal-full bg-[#121824] border-slate-700 text-slate-100 max-w-none max-h-[85vh] overflow-y-auto" data-testid="lettera-preview">
          {preview && (
            <>
              <DialogHeader><DialogTitle className="font-head">{preview.nome} <span className="text-xs font-normal text-slate-500">· dati aggiornati</span></DialogTitle></DialogHeader>
              <div className="flex items-center gap-3">
                <Select value={previewHead} onValueChange={setPreviewHead}>
                  <SelectTrigger className="w-48 h-9 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                    <SelectItem value="con">Con carta intestata</SelectItem>
                    <SelectItem value="senza">Senza carta intestata</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => {
                  const clean = (s) => (s || "").trim().replace(/[^\p{L}\p{N}\-]+/gu, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
                  const num = (pratica?.numero_pratica || "documento").replace(/\//g, "-");
                  const tpl = (preview.nome || "Documento").split("—")[0].trim();
                  const fileName = `${[clean(num), clean(pratica?.nome_assistito), clean(tpl)].filter(Boolean).join("_") || "documento"}.pdf`;
                  toast.promise(downloadPdf(fileName, previewRendered ?? preview.content, previewHead === "con", inte, globalMargins, { footer: pdfFooter }), { loading: "Generazione PDF…", success: "PDF scaricato", error: "Generazione PDF non riuscita" });
                }} disabled={previewRendering} data-testid="preview-print" className="ml-auto bg-cyan-600 hover:bg-cyan-700 text-white">
                  <Download className="h-4 w-4 mr-1" /> Scarica PDF
                </Button>
              </div>
              <div className="rounded-lg bg-slate-300/70 p-3 max-h-[58vh] overflow-y-auto" data-testid="preview-a4-wrap">
                {previewRendering ? (
                  <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
                ) : (
                  <LetterA4Preview contentHtml={previewRendered ?? preview.content} withHead={previewHead === "con"} inte={inte} margins={globalMargins} footer={pdfFooter} center />
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Storico versioni */}
      <Dialog open={!!versionsLetter} onOpenChange={(o) => !o && setVersionsLetter(null)}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg max-h-[80vh] overflow-y-auto" data-testid="versioni-dialog">
          {versionsLetter && (
            <>
              <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><History className="h-5 w-5 text-violet-400" /> Storico versioni · {versionsLetter.nome}</DialogTitle></DialogHeader>
              {(!versionsLetter.versions || versionsLetter.versions.length === 0) ? (
                <div className="py-6 text-center text-sm text-slate-500">Nessuna versione precedente. Le versioni si creano modificando e risalvando la lettera.</div>
              ) : (
                <div className="divide-y divide-slate-800">
                  {[...versionsLetter.versions].reverse().map((v, i) => (
                    <div key={i} className="flex items-center gap-3 py-2.5" data-testid={`versione-${i}`}>
                      <FileType2 className="h-5 w-5 text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-100 truncate">{v.nome}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{formatDateTime(v.saved_at)}</div>
                      </div>
                      <Button size="sm" onClick={() => restoreVersion(v)} data-testid={`versione-restore-${i}`} className="h-8 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                        <RotateCcw className="h-3.5 w-3.5 mr-1" /> Ripristina
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!folderEdit} onOpenChange={(o) => !o && setFolderEdit(null)}>
        <DialogContent className="max-w-md" data-testid="gl-folder-edit-dialog">
          <DialogHeader><DialogTitle>Modifica cartella</DialogTitle></DialogHeader>
          {folderEdit && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-mono">Nome cartella</Label>
                <Input noUppercase value={folderEdit.name} onChange={(e) => setFolderEdit({ ...folderEdit, name: e.target.value })} data-testid="gl-folder-edit-name" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-mono">Colore di sfondo</Label>
                <div className="mt-1.5"><FolderColorPicker value={folderEdit.colore} onChange={(c) => setFolderEdit({ ...folderEdit, colore: c })} testId="gl-folder-color" /></div>
              </div>
              <div className="flex justify-end">
                <Button onClick={saveFolderEdit} data-testid="gl-folder-edit-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Save className="h-4 w-4 mr-1" /> Salva cartella</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Folder picker: sposta / copia template in una cartella */}
      <Dialog open={!!folderPicker} onOpenChange={(o) => !o && setFolderPicker(null)}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-md" data-testid="template-folder-picker">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2">
              {folderPicker?.mode === "copy" ? <Copy className="h-5 w-5 text-emerald-400" /> : <FolderInput className="h-5 w-5 text-cyan-400" />}
              {folderPicker?.mode === "copy" ? "Copia template in cartella" : "Sposta template in cartella"}
            </DialogTitle>
          </DialogHeader>
          {folderPicker && (
            <>
              <p className="text-sm text-slate-400 -mt-1">
                {folderPicker.mode === "copy" ? "Scegli in quale cartella copiare" : "Scegli la cartella di destinazione per"} <span className="text-slate-200 font-medium">"{folderPicker.t.title}"</span>.
              </p>
              <div className="mt-2 pgp-card divide-y divide-slate-800 max-h-72 overflow-y-auto">
                <button type="button" onClick={() => confirmFolderPick("")} data-testid="folder-picker-none"
                  className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-slate-800/50 text-left">
                  <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                  <span className="text-sm text-slate-300">Senza cartella</span>
                </button>
                {folders.map((fo) => (
                  <button key={fo.id} type="button" onClick={() => confirmFolderPick(fo.id)} data-testid={`folder-picker-${fo.id}`}
                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-slate-800/50 text-left">
                    <Folder className="h-4 w-4 text-amber-400 shrink-0" />
                    <span className="text-sm text-slate-200 truncate">{fo.name}</span>
                    {folderPicker.mode === "move" && (folderPicker.t.folder_id || null) === fo.id && (
                      <span className="ml-auto text-[10px] text-slate-500 font-mono shrink-0">attuale</span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {letterPdfDoc && (
        <PdfPreviewModal
          doc={letterPdfDoc}
          title="Anteprima Lettera"
          filename={`${(editing?.nome || "lettera").replace(/[^\p{L}\p{N}\-_]+/gu, "_")}.pdf`}
          onClose={() => setLetterPdfDoc(null)}
        />
      )}

      <Dialog open={previewHtml !== null} onOpenChange={(o) => !o && setPreviewHtml(null)}>
        <DialogContent className="pgp-modal-full bg-white max-w-none max-h-[85vh] overflow-y-auto" data-testid="editor-preview-dialog">
          <DialogHeader><DialogTitle className="text-slate-900">Anteprima {previewLoading ? "" : "· dati pratica"}</DialogTitle></DialogHeader>
          {previewLoading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
          ) : (
            <div className="rounded bg-slate-300/70 p-3 max-h-[70vh] overflow-y-auto" data-testid="template-preview-a4">
              <LetterA4Preview contentHtml={previewHtml || ""} withHead={withHead} inte={inte} margins={editorMargins} footer={pdfFooter} center />
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!testiAsk} onOpenChange={(o) => { if (!o) setTestiAsk(null); }}>
        <DialogContent data-testid="testi-mode-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle>Più testimoni nella pratica</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Nella pratica ci sono {testiIdx.length} testimoni. Come vuoi generare la lettera?</p>
          <div className="flex flex-col gap-2 mt-2">
            <Button onClick={() => chooseTestiMode("each")} data-testid="testi-mode-each" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">Una lettera per ciascun testimone</Button>
            <Button onClick={() => chooseTestiMode("all")} data-testid="testi-mode-all" variant="outline">Tutti i testimoni nella stessa lettera</Button>
          </div>
        </DialogContent>
      </Dialog>
      <FirmaRemotaDialog open={!!firmaCtx} onOpenChange={(o) => !o && setFirmaCtx(null)} pratica={pratica} lettera={firmaCtx}
        defaultEmail={pratica?.assistito_scheda?.email || pratica?.assistito_email || ""} defaultName={assistitoName || pratica?.nome_assistito || ""} buildPdf={() => firmaCtx.buildPdf()} />
      <SendLetterEmailDialog open={emailOpen} onOpenChange={setEmailOpen} pratica={pratica} letterName={emailCtx?.nome || editing?.nome} assistitoName={assistitoName || pratica?.nome_assistito} clienteId={assistitoKey} buildLetterPdfB64={buildLetterPdfB64} letterHtml={emailCtx?.content ?? outContent(editing?.content ?? "")} letterId={emailCtx?.letterId ?? editing?.id} emailHead={emailCtx ? emailCtx.emailHead : (withHead ? inte : null)} onSent={() => { if (pratica?.id) api.get(lettereBase).then(({ data }) => setLetters(data)).catch(() => {}); }} />
    </>
  );
}
